const safeParseJson = (value) => {
    if (!value) {
        return null;
    }

    if (typeof value === "object") {
        return value;
    }

    if (typeof value !== "string") {
        return null;
    }

    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

const formatShareholderName = (shareholder = {}) => {
    if (shareholder?.companyName) {
        return shareholder.companyName;
    }

    return [
        shareholder?.title,
        shareholder?.firstName || shareholder?.name,
        shareholder?.lastName,
    ].filter(Boolean).join(" ");
};

const parseOwnershipGraph = (entities = [], relations = [], mainId) => {
    const entityMap = Object.fromEntries(
        entities
            .filter((entity) => entity?.cin)
            .map((entity) => [entity.cin, entity])
    );

    const mainEntity = entityMap[mainId];

    if (!mainEntity) {
        return null;
    }

    const shareholders = [];
    const subsidiaries = [];

    relations.forEach((relation) => {
        if (relation?.to === mainId && entityMap[relation.from]) {
            shareholders.push({
                cin: relation.from,
                name: entityMap[relation.from].name || "-",
                percentage: relation.percentage,
            });
        }

        if (relation?.from === mainId && entityMap[relation.to]) {
            subsidiaries.push({
                cin: relation.to,
                name: entityMap[relation.to].name || "-",
                percentage: relation.percentage,
            });
        }
    });

    return {
        main: {
            cin: mainId,
            name: mainEntity.name || "-",
        },
        shareholders,
        subsidiaries,
    };
};

const buildFallbackGraph = (supplierData = {}) => {
    const supplierDetail = supplierData?.supplierDetail || {};
    const mainCin = "main-company";
    const mainName = supplierDetail?.name || "-";

    const shareholders = [];
    const subsidiaries = [];

    const companyShareholders = Array.isArray(supplierData?.shareHolders?.company)
        ? supplierData.shareHolders.company
        : [];
    const individualShareholders = Array.isArray(supplierData?.shareHolders?.individual)
        ? supplierData.shareHolders.individual
        : [];

    [...companyShareholders, ...individualShareholders].forEach((shareholder, index) => {
        const name = formatShareholderName(shareholder);

        if (!name) {
            return;
        }

        shareholders.push({
            cin: shareholder?.id || `shareholder-${index}`,
            name,
            percentage: shareholder?.percentage ?? shareholder?.sharePercent,
        });
    });

    const connectedEntities = Array.isArray(supplierData?.connectedLegalEntities)
        ? supplierData.connectedLegalEntities
        : [];

    connectedEntities.forEach((entity, index) => {
        const name = entity?.name || entity?.shortName;

        if (!name) {
            return;
        }

        subsidiaries.push({
            cin: entity?.id || entity?.mandateId || `subsidiary-${index}`,
            name,
            percentage: entity?.percentage ?? entity?.ownershipPercentage,
        });
    });

    return {
        main: {
            cin: mainCin,
            name: mainName,
        },
        shareholders,
        subsidiaries,
        source: "fallback",
    };
};

const resolveGroupStructureGraph = (supplierData = {}) => {
    const rawGraph = supplierData?.groupStructureGraph
        ?? supplierData?.shareholderGraphData
        ?? supplierData?.supplierDetail?.groupStructureGraph
        ?? supplierData?.supplierDetail?.shareholderGraphData;

    const graph = safeParseJson(rawGraph);

    if (graph?.entities?.length && graph?.from_cin) {
        const parsed = parseOwnershipGraph(
            graph.entities,
            graph.relations || [],
            graph.from_cin
        );

        if (parsed) {
            return {
                ...parsed,
                source: "graph",
            };
        }
    }

    return buildFallbackGraph(supplierData);
};

const formatOwnershipPercentage = (percentage) => {
    if (percentage === null || percentage === undefined || percentage === "") {
        return null;
    }

    const numericValue = Number(percentage);

    if (Number.isNaN(numericValue)) {
        return null;
    }

    return `${numericValue.toFixed(2)}%`;
};

const GROUP_STRUCTURE_COLUMNS = [
    { key: "index", label: "#", width: "8%" },
    { key: "name", label: "Entity", width: "52%" },
    { key: "role", label: "Role", width: "22%" },
    { key: "ownership", label: "Ownership", width: "18%" },
];

const buildGroupStructureRecords = (graphData) => {
    const records = [];

    graphData?.shareholders?.forEach((shareholder, index) => {
        records.push({
            id: shareholder?.id || `shareholder-${index}`,
            index: records.length + 1,
            name: shareholder?.name,
            role: "Shareholder",
            ownership: formatOwnershipPercentage(shareholder?.percentage),
        });
    });

    if (graphData?.main?.name) {
        records.push({
            id: graphData.main?.id || "main-company",
            index: records.length + 1,
            name: graphData.main.name,
            role: "Main Company",
            ownership: null,
        });
    }

    graphData?.subsidiaries?.forEach((subsidiary, index) => {
        records.push({
            id: subsidiary?.id || `subsidiary-${index}`,
            index: records.length + 1,
            name: subsidiary?.name,
            role: "Subsidiary",
            ownership: formatOwnershipPercentage(subsidiary?.percentage),
        });
    });

    return records;
};

export {
    resolveGroupStructureGraph,
    formatOwnershipPercentage,
    buildGroupStructureRecords,
    GROUP_STRUCTURE_COLUMNS,
};
